import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  format,
  isBefore,
  isValid,
  isAfter
} from 'date-fns';
import { pl } from 'date-fns/locale';
import axios from 'axios';
import Page from '../molecules/Page';
import {
  MdCheck,
  MdArrowBack,
  MdRestore,
  MdOutlineAccessTime,
  MdClose,
  MdSecurity,
} from 'react-icons/md';
import { Capacitor } from '@capacitor/core';
import api from '../../api/apiHelper';
import { persistDashboardData } from '../../utils/apiCache';
import {
  getFormattedPrices,
  getRawPricesInGrosze,
  getMaturaDate,
  getMaturaAccessEndDate,
} from '../../utils/configUtils';
import LearningSystemSheet from '../molecules/LearningSystemSheet';
import StoreReviews from '../molecules/StoreReviews';
import { trackPurchase } from '../../api/tiktokEvents';
import posthog from 'posthog-js';

let Purchases, LOG_LEVEL, PURCHASES_ERROR_CODE;
if (Capacitor.isNativePlatform()) {
  import('@revenuecat/purchases-capacitor').then(module => {
    Purchases = module.Purchases;
    LOG_LEVEL = module.LOG_LEVEL;
    PURCHASES_ERROR_CODE = module.PURCHASES_ERROR_CODE;
  });
}

const IS_MOBILE_BUILD = process.env.REACT_APP_IS_MOBILE === 'true';
const PURCHASE_TIMEOUT_MS = 600_000; // 10 minut — dodawanie karty / BLIK / rodzic trwa dłużej niż 2 min
const POPRAWKA_DATE = new Date('2026-08-31T23:59:59Z');

const getRevenueCatApiKey = () => {
  if (!IS_MOBILE_BUILD) {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") return process.env.REACT_APP_REVENUECAT_APPLE_KEY;
    if (platform === "android") return process.env.REACT_APP_REVENUECAT_GOOGLE_KEY;
  } else {
    const platform = Capacitor.getPlatform();
    if (platform === "ios") return process.env.REACT_APP_REVENUECAT_APPLE_KEY;
    return process.env.REACT_APP_REVENUECAT_GOOGLE_KEY;
  }
  return null;
};

const REVENUECAT_API_KEY = getRevenueCatApiKey();

// Nazwy identyczne z Product ID w App Store Connect / Google Play (RevenueCat) —
// dla PP+PR jest to "pp_pr", nie "combo", bo Product ID w ASC jest niemodyfikowalny
// po utworzeniu. `pricing.py`/`configUtils.js` po stronie web uzywaja tych samych stringow.
const PRODUCT_IDS = {
  ONE_MONTH: '1_miesiac',
  ONE_MONTH_PR: '1_miesiac_pr',
  ONE_MONTH_PP_PR: '1_miesiac_pp_pr',
  THREE_MONTHS: '3_miesiace',
  MATURA_PP_2027_REGULAR: 'matura_pp_2027_regular',
  MATURA_PP_2027_PROMO: 'matura_pp_2027_promo',
  MATURA_PR_2027_REGULAR: 'matura_pr_2027_regular',
  MATURA_PR_2027_PROMO: 'matura_pr_2027_promo',
  MATURA_PP_PR_2027_REGULAR: 'matura_pp_pr_2027_regular',
  MATURA_PP_PR_2027_PROMO: 'matura_pp_pr_2027_promo',
  MATURA_POPRAWKA: 'matura_poprawka',
};

const PREMIUM_ENTITLEMENT = 'premium';

// --- KOMPONENT LICZNIKA ---
const COUNTDOWN_THRESHOLD_DAYS = 7;

const OfferCountdown = ({ targetDate, oldPrice, className = 'mt-1 text-sm text-gray-500', prefix = 'Cena powitalna kończy się za ' }) => {
  const [timeLeft, setTimeLeft] = useState("");
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff <= 0) {
        setDaysLeft(0);
        setTimeLeft("00:00:00");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      setDaysLeft(days);
      if (days <= COUNTDOWN_THRESHOLD_DAYS) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (daysLeft === null) return null;

  if (daysLeft > COUNTDOWN_THRESHOLD_DAYS) {
    const dateStr = format(targetDate, "d MMMM yyyy", { locale: pl });
    return (
      <p className={className}>
        Cena powitalna do <span className="font-bold text-[#233855]">{dateStr}</span>{oldPrice && `, potem ${oldPrice}`}
      </p>
    );
  }

  return (
    <p className={className}>
      {oldPrice ? `Cena powitalna - wraca do ${oldPrice} za ` : prefix}
      <span className="font-bold tabular-nums text-[#233855]">{timeLeft}</span>
    </p>
  );
};

// Bullet karty: string (HTML) albo { title, sub } — haslo + linia pod spodem.
const FeatureItem = ({ feature, checkClassName }) => (
  <li className="flex items-start text-sm text-gray-700">
    <MdCheck className={`mr-2 h-5 w-5 flex-shrink-0 ${checkClassName}`} />
    {typeof feature === 'string' ? (
      <span dangerouslySetInnerHTML={{ __html: feature }} />
    ) : (
      <div>
        <p className="font-semibold text-[#233855]">{feature.title}</p>
        <p className="mt-0.5 text-xs text-gray-500">{feature.sub}</p>
      </div>
    )}
  </li>
);

// --- WIERSZ STATUSU DOSTĘPU (ten sam wygląd na webie i w aplikacji) ---
const AccessStatusRow = ({ name, verb, until }) => (
  <div className="flex items-center gap-2.5 py-2.5 border-t border-[#007D00]/10 first:border-t-0">
    <span className="flex-none w-[22px] h-[22px] rounded-full bg-[#007D00] text-white grid place-items-center">
      <MdCheck className="text-sm" />
    </span>
    <span className="flex-1 font-apotek text-[15px] uppercase tracking-wider text-[#233855]">{name}</span>
    <span className="text-xs text-gray-500">
      {verb} do <span className="font-semibold text-[#233855]">{format(until, 'd MMMM yyyy', { locale: pl })}</span>
    </span>
  </div>
);

// --- WIERSZ WYBORU PLANU (radio) ---
const PlanRow = ({ offer, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`relative w-full flex items-center gap-3 text-left rounded-2xl border-2 px-4 py-2.5 transition-colors ${selected ? 'border-[#E09F2E] bg-[#FFFCF6] shadow-[0_0_0_3px_#FBF0DC]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
  >
    {offer.badge && (
      <span className="absolute top-0 right-4 -translate-y-1/2 rounded-full bg-[#E09F2E] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
        {offer.badge}
      </span>
    )}
    <span className={`flex-none w-[22px] h-[22px] rounded-full border-2 grid place-items-center ${selected ? 'border-[#E09F2E]' : 'border-gray-300'}`}>
      {selected && <span className="w-[11px] h-[11px] rounded-full bg-[#E09F2E]" />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-apotek text-base uppercase tracking-wider leading-tight text-[#233855]">{offer.name}</span>
      {offer.note && <span className="block mt-0.5 text-xs font-semibold text-[#2E8B57]">{offer.note}</span>}
    </span>
    <span className="flex-none text-right tabular-nums">
      {offer.oldPrice && <span className="block text-xs text-gray-400 line-through">{offer.oldPrice}</span>}
      <span className="block text-lg font-bold text-[#233855] whitespace-nowrap">{offer.price}</span>
    </span>
  </button>
);

// Oszczędność pakietu względem osobnych planów (w zł) — tylko gdy realnie > 0.
const savingsNote = (pp, pr, combo) => {
  const saved = Math.round((pp || 0) + (pr || 0) - (combo || 0));
  return pp && pr && combo && saved > 0 ? `${saved} zł taniej niż osobno` : null;
};

const NavyPlanCard = ({ title, badge, price, accessUntil, features = [], onSelect, disabled = false }) => (
  <div className="relative flex flex-col rounded-2xl p-6 bg-white border-2 border-[#233855]">
    <div className="absolute top-0 left-[10%] -translate-y-1/2 rounded-full bg-[#233855] px-4 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-lg flex items-center">
      {badge}
    </div>
    <h3 className="font-apotek text-lg uppercase tracking-widest text-[#233855]">{title}</h3>
    <div className="mt-2">
      <span className="text-4xl font-bold text-[#233855]">{price || 'Cena...'}</span>
    </div>
    {accessUntil && (
      <div className="mt-3 flex items-center text-sm text-gray-600">
        <MdOutlineAccessTime className="mr-2 h-5 w-5 flex-shrink-0 text-gray-400" />
        <span className="font-medium">{accessUntil}</span>
      </div>
    )}
    <button
      onClick={onSelect}
      disabled={disabled}
      className="mt-6 w-full rounded-lg px-4 py-3 font-apotek text-sm font-bold uppercase tracking-widest text-white bg-[#233855] hover:bg-[#345081] shadow-md transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
    >
      {disabled ? 'Przetwarzanie...' : 'Wybieram'}
    </button>
    {features.length > 0 && (
      <ul className="mt-6 space-y-3">
        {features.map((feature, index) => (
          <FeatureItem key={index} feature={feature} checkClassName="text-[#233855]" />
        ))}
      </ul>
    )}
  </div>
);


const MOCK_IOS_PACKAGES = [
  { product: { identifier: 'do_matury', title: 'Do Matury (Matematly)', priceString: '79,99 zł', description: 'Dostęp do matury 2025' } },
  { product: { identifier: '3_miesiace', title: '3 Miesiące (Matematly)', priceString: '39,99 zł', description: 'Dostęp przez 3 miesiące' } },
  { product: { identifier: '1_miesiac', title: '1 Miesiąc (Matematly)', priceString: '17,99 zł', description: 'Dostęp przez 1 miesiąc' } },
];

const Upgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isPreviewIOS = new URLSearchParams(location.search).get('preview') === 'ios';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [purchaseInitiatedTimestamp, setPurchaseInitiatedTimestamp] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const ACCESS_EXPIRES_DATE = getMaturaDate();
  const ACCESS_END_DATE = getMaturaAccessEndDate();
  const prices = getFormattedPrices();
  const rawPrices = getRawPricesInGrosze();

  const [baseDate, setBaseDate] = useState(() => new Date());
  const isPoprawkaExpired = !isBefore(baseDate, POPRAWKA_DATE);
  const hasMaturaAccess = !isBefore(baseDate, ACCESS_EXPIRES_DATE);

  // Oferta powitalna: ceny promo tylko do `promo_offer_expires_at` z profilu
  // (koniec dnia rejestracji). Startowo z cache, zeby karty nie przeskakiwaly
  // z ceny regularnej na promo po odpowiedzi serwera.
  const [promoExpiresAt, setPromoExpiresAt] = useState(() => {
    try {
      const ts = JSON.parse(localStorage.getItem('userStatus'))?.promo_offer_expires_at;
      return ts && ts * 1000 > Date.now() ? new Date(ts * 1000) : null;
    } catch {
      return null;
    }
  });
  const isPromoActive = !!promoExpiresAt;
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [offerType, setOfferType] = useState('STANDARD');

  // Rozszerzenie to osobny produkt — ma własną datę wygaśnięcia w profilu.
  const [extendedExpiresAt, setExtendedExpiresAt] = useState(null);
  // Karty "do matury" chowamy dopiero, gdy rozszerzenie siega SAMEJ matury.
  // Dostep krotszy (trial, wariant miesieczny) ma dalej widziec pelna oferte —
  // wczesniej `hasExtendedAccess` chowal ja przed najlepiej zakwalifikowanym
  // klientem: trialowiec nie mial jak kupic PR, a miesieczny nie mial jak doplacic.
  const hasMaturaExtendedAccess =
    !!extendedExpiresAt && !isBefore(extendedExpiresAt, ACCESS_EXPIRES_DATE);

  // Domyślnie zaznaczony plan: pakiet PP+PR, gdy user w onboardingu zadeklarował oba poziomy,
  // w przeciwnym razie podstawa.
  const [declaresPr, setDeclaresPr] = useState(() => {
    try {
      return !!JSON.parse(localStorage.getItem('userStatus'))?.declares_pr;
    } catch {
      return false;
    }
  });
  const featureCombo = declaresPr && !hasMaturaExtendedAccess;
  const ownsSomePlan = hasMaturaAccess || hasMaturaExtendedAccess;
  const ownsAllPlans = hasMaturaAccess && hasMaturaExtendedAccess;
  // null = domyślny plan (pakiet dla deklarujących PR, inaczej podstawa).
  const [selectedPlan, setSelectedPlan] = useState(null);

  // --- FIX #3: useRef dla listenera — unikamy stale closure ---
  const purchaseTimestampRef = useRef(null);
  const purchaseTimeoutRef = useRef(null);

  // Zakup potrafi sie zamknac dwiema droga naraz (Promise i listener). Trzymamy tu pakiet
  // od startu platnosci i zerujemy przy raportowaniu, zeby TikTok dostal Purchase tylko raz.
  const pendingPurchaseRef = useRef(null);

  const reportPurchaseToTikTok = useCallback(() => {
    const pkg = pendingPurchaseRef.current;
    if (!pkg) return;
    pendingPurchaseRef.current = null;

    trackPurchase({
      productId: pkg.product.identifier,
      value: pkg.product.price,
      currency: pkg.product.currencyCode,
    });
  }, []);

  // --- ANALITYKA: View (Krok 1) ---
  useEffect(() => {
      if (offerType) {
          posthog.capture('checkout_viewed', {
              offer_type: offerType,
              platform: Capacitor.getPlatform(),
              is_mobile_build: IS_MOBILE_BUILD,
              has_promo: offerType !== 'STANDARD'
          });
      }
  }, [offerType]);

  const syncUserData = useCallback(async () => {
    const now = new Date();
    try {
      console.log("Upgrade.js: Syncing user data...");
      const { data } = await api.get("user_dashboard_data/");
      
      persistDashboardData(data);

      if (data.account_status) {
        const timestamp = data.account_status.access_expires_at_timestamp;
        
        if (timestamp) {
          const currentExpiry = new Date(timestamp * 1000);
          if (isValid(currentExpiry) && isAfter(currentExpiry, now)) {
            setBaseDate(currentExpiry);
          } else {
            setBaseDate(now);
          }
        } else if (data.account_status.access_expires_at) {
           const currentExpiry = new Date(data.account_status.access_expires_at);
           if (isValid(currentExpiry) && isAfter(currentExpiry, now)) {
            setBaseDate(currentExpiry);
           } else {
             setBaseDate(now);
           }
        }

        // `pr_expires_at` niesie prawdziwa date; starsze pole jest zamaskowane pod
        // wydane appki, wiec zostaje tylko jako fallback na starszy backend.
        const extendedTs = data.account_status.pr_expires_at
          ?? data.account_status.access_expires_at_pr_timestamp;
        if (extendedTs) {
          const extendedDate = new Date(extendedTs * 1000);
          setExtendedExpiresAt(isValid(extendedDate) ? extendedDate : null);
        } else {
          setExtendedExpiresAt(null);
        }

        setDeclaresPr(!!data.account_status.declares_pr);

        const promoTimestamp = data.account_status.promo_offer_expires_at;

        let promoDate = null;
        if (promoTimestamp) {
            promoDate = new Date(promoTimestamp * 1000);
            if (!isValid(promoDate)) promoDate = null;
        }

        if (promoDate && isAfter(promoDate, now)) {
             setPromoExpiresAt(promoDate);
             setOfferType('WELCOME');
        } else {
             setPromoExpiresAt(null);
             setOfferType('STANDARD');
        }

        return true;
      }
    } catch (err) {
      console.error("Failed to sync user data in Upgrade:", err);
      try {
        const userStatusString = localStorage.getItem('userStatus');
        if (userStatusString) {
          const userStatus = JSON.parse(userStatusString);
          const timestamp = userStatus?.access_expires_at_timestamp;
          if (timestamp) {
            const currentExpiry = new Date(timestamp * 1000);
            if (isValid(currentExpiry) && isAfter(currentExpiry, now)) {
              setBaseDate(currentExpiry);
            }
          }
        }
      } catch (e) {}
      return false;
    }
  }, []);

  useEffect(() => {
    syncUserData();
  }, [syncUserData, location]);

  const [isNative, setIsNative] = useState(IS_MOBILE_BUILD);
  const [packages, setPackages] = useState([]); 

  // --- FIX #3: Listener używa ref zamiast state — nigdy nie ma stale closure ---
  const handleCustomerInfo = useCallback(async (customerInfo) => {
    const hasPremium = typeof customerInfo.entitlements.active[PREMIUM_ENTITLEMENT] !== "undefined";
    
    // Używamy ref — zawsze aktualny, bez stale closure
    if (!purchaseTimestampRef.current) return;

    const latestTransaction = [...(customerInfo.nonSubscriptionTransactions || [])]
      .sort((a, b) => b.purchaseDateMillis - a.purchaseDateMillis)[0];

    if (hasPremium && latestTransaction && latestTransaction.purchaseDateMillis >= purchaseTimestampRef.current) {
      console.log("Upgrade.js: Listener wykrył zakup.");
      
      posthog.capture('checkout_native_success_listener', {
          platform: Capacitor.getPlatform()
      });

      reportPurchaseToTikTok();

      // Czyścimy ref i timeout
      purchaseTimestampRef.current = null;
      setPurchaseInitiatedTimestamp(null);
      if (purchaseTimeoutRef.current) {
        clearTimeout(purchaseTimeoutRef.current);
        purchaseTimeoutRef.current = null;
      }

      await syncUserData();
      navigate('/payment-status');
    }
  }, [navigate, syncUserData, reportPurchaseToTikTok]);

  // --- FIX #4: Listener rejestrowany PO configure, wewnątrz setupRevenueCat ---
  const setupRevenueCat = useCallback(async () => {
    if (!IS_MOBILE_BUILD && !Capacitor.isNativePlatform()) return;
    setIsLoading(true);
    try {
      if (!Purchases) {
         if (IS_MOBILE_BUILD) {
           const module = await import('@revenuecat/purchases-capacitor');
           Purchases = module.Purchases;
           LOG_LEVEL = module.LOG_LEVEL;
           PURCHASES_ERROR_CODE = module.PURCHASES_ERROR_CODE;
         } else {
           setError("System unavailable.");
           return;
         }
      }

      Purchases.setLogLevel(LOG_LEVEL.DEBUG);

      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setError("User ID missing.");
        setIsLoading(false); 
        return;
      }

      await Purchases.configure({ apiKey: REVENUECAT_API_KEY, appUserID: userId });

      // FIX #4: Rejestrujemy listener PO configure — gwarancja że Purchases jest gotowe
      Purchases.addCustomerInfoUpdateListener(handleCustomerInfo);

      const offerings = await Purchases.getOfferings();
      const myOffer = offerings.all['matematly_offer'];

      console.log('[RC] all offerings:', Object.keys(offerings.all));
      console.log('[RC] matematly_offer packages:', myOffer?.availablePackages?.map(p => p.product.identifier) ?? 'BRAK OFERTY');

      if (myOffer && myOffer.availablePackages.length > 0) {
        setPackages(myOffer.availablePackages);
        
        posthog.capture('checkout_native_packages_loaded', {
            packages_count: myOffer.availablePackages.length,
            package_ids: myOffer.availablePackages.map(p => p.product.identifier)
        });
      } else {
        setError("No packages available.");
        posthog.capture('checkout_native_packages_error', { reason: 'no_packages' });
      }
    } catch (e) {
      // FIX #2: Naprawiony setError — nawiasy zamiast tagged template
      setError(`Init error: ${e.message}`);
      posthog.capture('checkout_native_init_error', { error: e.message });
    } finally {
      setIsLoading(false);
    }
  }, [handleCustomerInfo]);

  useEffect(() => {
    const checkPlatform = async () => {
      if (Capacitor.isNativePlatform() || IS_MOBILE_BUILD) {
        setIsNative(true);
        // FIX #4: Listener jest teraz wewnątrz setupRevenueCat, nie tutaj
        setTimeout(() => {
          setupRevenueCat();
        }, 100);
      }
    };
    checkPlatform();

    return () => {
      if ((Capacitor.isNativePlatform() || IS_MOBILE_BUILD) && Purchases) {
        Purchases.removeCustomerInfoUpdateListener(handleCustomerInfo);
      }
      // Cleanup timeout przy unmount
      if (purchaseTimeoutRef.current) {
        clearTimeout(purchaseTimeoutRef.current);
      }
    };
  }, [setupRevenueCat, handleCustomerInfo]);

  // --- GŁÓWNA LOGIKA ZAKUPU (NATIVE) ---
  const handleNativePurchase = async (pkg) => {
    if (!pkg) {
      setError("Niedostępne.");
      return;
    }

    posthog.capture('checkout_native_purchase_started', {
        product_id: pkg.product.identifier,
        price: pkg.product.priceString,
        offer_type: offerType
    });
    
    // Ustawiamy ZARÓWNO ref (dla listenera) JAK I state (dla UI)
    const now = Date.now();
    purchaseTimestampRef.current = now;
    pendingPurchaseRef.current = pkg;
    setPurchaseInitiatedTimestamp(now);
    setIsLoading(true);
    setError('');

    // --- FIX #1: TIMEOUT — jeśli sklep nie odpowie w PURCHASE_TIMEOUT_MS, odblokowuj UI ---
    const storeName = Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Google Play';
    purchaseTimeoutRef.current = setTimeout(() => {
      console.warn(`Upgrade.js: Purchase timeout — ${storeName} nie odpowiedział.`);
      setIsLoading(false);
      setShowSummaryModal(false);
      setError(`${storeName} nie odpowiedział. Spróbuj ponownie lub uruchom ponownie aplikację. Jeśli pieniądze zostały pobrane, użyj "Przywróć zakupy".`);
      posthog.capture('checkout_native_timeout', {
          product_id: pkg.product.identifier,
          timeout_ms: PURCHASE_TIMEOUT_MS,
          platform: Capacitor.getPlatform()
      });
    }, PURCHASE_TIMEOUT_MS);

    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      
      // Promise się rozwiązał — czyścimy timeout
      clearTimeout(purchaseTimeoutRef.current);
      purchaseTimeoutRef.current = null;

      const hasPremium = typeof customerInfo.entitlements.active[PREMIUM_ENTITLEMENT] !== "undefined";

      if (hasPremium) {
          console.log("Upgrade.js: Zakup udany (Promise). Przekierowanie...");
          
          posthog.capture('checkout_native_success_direct', {
              product_id: pkg.product.identifier
          });

          reportPurchaseToTikTok();

          purchaseTimestampRef.current = null;
          setPurchaseInitiatedTimestamp(null);
          setShowSummaryModal(false); 
          await syncUserData(); 
          navigate('/payment-status');
          return; 
      } else {
          // Zakup przeszedł ale brak entitlement — pending
          console.warn("Upgrade.js: Zakup udany, ale oczekuje na weryfikację (Pending).");
          posthog.capture('checkout_native_success_pending', {
              product_id: pkg.product.identifier,
              platform: Capacitor.getPlatform()
          });
          
          setShowSummaryModal(false);
          const pendingMsg = Capacitor.getPlatform() === 'ios'
            ? "Twoja płatność oczekuje na zatwierdzenie (np. przez rodzica w ramach Ask to Buy lub weryfikację banku). Dostęp zostanie przyznany automatycznie po zatwierdzeniu. Jeśli to potrwa dłużej, użyj 'Przywróć zakupy'."
            : "Twoja płatność oczekuje na weryfikację przez sklep (np. bank). Dostęp zostanie przyznany automatycznie w ciągu kilku minut. Jeśli to potrwa dłużej, użyj przycisku 'Przywróć zakupy'.";
          alert(pendingMsg);
      }
    } catch (e) {
      // Promise reject — czyścimy timeout
      clearTimeout(purchaseTimeoutRef.current);
      purchaseTimeoutRef.current = null;

      if (e.code === PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR) {
        purchaseTimestampRef.current = null;
        pendingPurchaseRef.current = null;
        setPurchaseInitiatedTimestamp(null);
        posthog.capture('checkout_native_cancelled', {
             product_id: pkg.product.identifier
        });
      } else if (e.code === PURCHASES_ERROR_CODE?.PAYMENT_PENDING_ERROR) {
         console.warn("Płatność oczekująca (Pending Error).");
         posthog.capture('checkout_native_pending_error', { 
           product_id: pkg.product.identifier,
           platform: Capacitor.getPlatform()
         });
         
         setShowSummaryModal(false);
         const pendingErrMsg = Capacitor.getPlatform() === 'ios'
           ? "Płatność oczekuje na zatwierdzenie (np. Ask to Buy). Dostęp zostanie nadany po potwierdzeniu. Jeśli to potrwa dłużej, użyj 'Przywróć zakupy'."
           : "Płatność oczekuje na zatwierdzenie przez bank. Dostęp zostanie nadany po potwierdzeniu. Jeśli to potrwa dłużej, użyj 'Przywróć zakupy'.";
         alert(pendingErrMsg);
      } else if (e.code === PURCHASES_ERROR_CODE?.PURCHASE_NOT_ALLOWED_ERROR) {
         // iOS: zakupy zablokowane przez kontrolę rodzicielską / MDM
         posthog.capture('checkout_native_not_allowed', { 
           product_id: pkg.product.identifier,
           platform: Capacitor.getPlatform()
         });
         setShowSummaryModal(false);
         alert(Capacitor.getPlatform() === 'ios'
           ? "Zakupy są zablokowane na tym urządzeniu. Sprawdź ustawienia kontroli rodzicielskiej w Ustawieniach > Czas przed ekranem, lub poproś rodzica o zgodę."
           : "Zakupy są zablokowane na tym urządzeniu. Sprawdź ustawienia konta Google Play."
         );
      } else if (e.code === PURCHASES_ERROR_CODE?.STORE_PROBLEM_ERROR) {
         // Problemy ze sklepem (App Store / Google Play down)
         posthog.capture('checkout_native_store_problem', { 
           product_id: pkg.product.identifier,
           platform: Capacitor.getPlatform()
         });
         const store = Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Google Play';
         setError(`${store} ma chwilowe problemy. Spróbuj ponownie za kilka minut.`);
      } else {
        // FIX #2: Naprawiony setError — nawiasy zamiast tagged template
        setError(`Wystąpił błąd: ${e.message}`);
        
        posthog.capture('checkout_native_error', {
            error_code: e.code,
            error_message: e.message,
            product_id: pkg.product.identifier
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestorePurchases = async () => {
    setIsLoading(true);
    setError('');
    
    posthog.capture('checkout_restore_initiated');
    try {
      const customerInfo = await Purchases.restorePurchases();
      
      const activeSubs = customerInfo.activeSubscriptions || [];
      const nonSubs = customerInfo.nonSubscriptionTransactions || [];
      const activeEntitlements = customerInfo.entitlements?.active || {};

      if (activeSubs.length > 0 || Object.keys(activeEntitlements).length > 0 || nonSubs.length > 0) {
          posthog.capture('checkout_restore_success');
          await syncUserData();
          navigate('/payment-status');
      } else {
          alert("Nie znaleziono zakupów do przywrócenia.");
          posthog.capture('checkout_restore_empty');
      }
    } catch (e) {
      if (e.message && (e.message.includes("No active account") || e.code === 10)) {
        alert("Zaloguj się w App Store/Sklep Play.");
      } else {
        // FIX #2: Naprawiony setError — nawiasy zamiast tagged template
        setError(`Błąd przywracania: ${e.message}`);
      }
      posthog.capture('checkout_restore_error', { error: e.message });
    } finally {
      setIsLoading(false);
    }
  };

  const performWebAction = IS_MOBILE_BUILD 
    ? () => {} 
    : async (planId, amountInZloty) => {
      posthog.capture('checkout_web_purchase_started', {
          plan_id: planId,
          amount: amountInZloty,
          offer_type: offerType
      });

      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Sesja wygasła.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_SOURCE}create-payment-order/`,
          { planId, amount: amountInZloty },
          { headers: { 'Authorization': `Token ${token}` } }
        );
        if (response.data && response.data.redirectUri) {
          posthog.capture('checkout_web_redirecting', {
              url: response.data.redirectUri
          });
          window.location.href = response.data.redirectUri;
        } else {
          setError('Błąd serwera.');
          setIsLoading(false);
          posthog.capture('checkout_web_error', { reason: 'no_redirect_uri' });
        }
      } catch (err) {
        setError('Błąd serwera.');
        setIsLoading(false);
        posthog.capture('checkout_web_error', { error: err.message });
      }
    };

  const handleOpenSummary = (planData) => {
    posthog.capture('checkout_plan_selected', {
        plan_title: planData.title,
        price_display: planData.price,
        offer_type: offerType
    });
    setError('');
    setSummaryData(planData);
    setTermsAccepted(false); 
    setShowSummaryModal(true);
  };

  const handleConfirmPurchase = () => {
    if (!termsAccepted) return;
    
    posthog.capture('checkout_summary_confirmed', {
        plan_title: summaryData?.title
    });

    if (summaryData && summaryData.action) {
      summaryData.action();
    }
  };
  
  // --- FEATURES ---
  // „Lekcje” liczone z pricing.py (promo i regular) przy korepetycjach 100–180 zł/h — po zmianie cen przeliczyć.
  const featuresPP = [
      { title: 'Pewniaki maturalne', sub: 'System nauki oparty na analizie arkuszy CKE' },
      { title: 'Tylko to, co będzie na maturze', sub: 'Zadania typu CKE z kartą wzorów przy każdym' },
      { title: 'Taniej niż korepetycje', sub: 'Nauka do maja w cenie 1–2 lekcji, w swoim tempie' },
  ];

  const featuresPR = [
      { title: 'Niepewniaki Rozszerzenia', sub: 'System nauki oparty na analizie arkuszy PR' },
      // { title: 'Tylko to, co będzie na rozszerzeniu', sub: 'Pochodne, Viète, Bayes, dowody — zadania typu CKE' },
      { title: 'Wynik, który liczy się w rekrutacji', sub: 'Każdy procent z rozszerzenia to punkty na studia' },
      { title: 'Taniej niż korepetycje', sub: 'Rozszerzenie do maja w cenie 1–2 lekcji' },
  ];

  const featuresCombo = [
      { title: 'Wszystko z Podstawy', sub: 'Pewniaki, zadania typu maturalnego, Karta Wzorów CKE' },
      { title: 'Niepewniaki Rozszerzenia', sub: 'System nauki oparty na analizie arkuszy PR' },
      { title: 'Wynik, który liczy się w rekrutacji', sub: 'Każdy procent z rozszerzenia to punkty na studia' },
      { title: 'Taniej niż korepetycje', sub: 'Oba poziomy do maja w cenie 2–3 lekcji' },
  ];

  const featuresPoprawka = [
      "<strong>Dostęp do 31 sierpnia 2026</strong>",
      "<strong>Masz czas, żeby zdać maturę</strong>",
      "Spersonalizowana nauka",
      "Tylko zadania typu maturalnego",
      "Integracja z Kartą Wzorów",
      "Inteligentne Arkusze",
      "Asystent Orzeł AI",
      "Trening dowolny (pełna kontrola)",
  ];

  // --- LOGIKA WYBORU PRODUKTU (native, PP) ---
  // Uzywane tylko przez renderNativeOptions — web liczy wlasne ceny bezposrednio w renderWebOptions.
  const getMaturaProduct = () => {
    // Blokuj tylko gdy data matury jest w przyszłości (prawidłowy config)
    // i dostęp użytkownika już ją przekracza. Stary cache z 2026 nie blokuje.
    if (isBefore(new Date(), ACCESS_EXPIRES_DATE) && !isBefore(baseDate, ACCESS_EXPIRES_DATE)) return null;
    if (!isNative) return null;

    const regularPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PP_2027_REGULAR);
    const promoPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PP_2027_PROMO);
    const activePkg = (isPromoActive && promoPkg) ? promoPkg : regularPkg;

    if (!activePkg) return null;

    return {
        isNative: true,
        pkg: activePkg,
        title: "Podstawa 2027",
        price: activePkg.product.priceString,
        oldPrice: (isPromoActive && promoPkg && regularPkg) ? regularPkg.product.priceString : null,
        isSpecial: isPromoActive && !!promoPkg,
        expiresAt: isPromoActive ? promoExpiresAt : null,
        features: featuresPP,
    };
  };

  const maturaPlan = getMaturaProduct();

  // --- WYBÓR PLANU: wiersze PP / pakiet / PR, jeden przycisk, bullety wybranego planu ---
  // offers: [{ key, name, price, oldPrice, note, badge, features, onBuy }], kolejność stała.
  const renderPlanPicker = (offers) => {
    const available = offers.filter(Boolean);
    if (available.length === 0) return null;
    const keys = available.map(o => o.key);
    const defaultKey = featureCombo && keys.includes('combo') ? 'combo' : keys[0];
    const selected = available.find(o => o.key === (keys.includes(selectedPlan) ? selectedPlan : defaultKey));

    return (
      <div className="w-full">
        {isPromoActive && (
          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#E09F2E]">Cena dla ambitnych</p>
            <OfferCountdown
              targetDate={promoExpiresAt}
              prefix={selected.oldPrice ? `Cena wraca do ${selected.oldPrice} za ` : 'Kończy się za '}
              className="mt-0.5 text-sm text-[#233855]"
            />
          </div>
        )}
        <div role="radiogroup" aria-label="Plan" className="mt-3.5 flex flex-col gap-2.5">
          {available.map(o => (
            <PlanRow
              key={o.key}
              offer={o}
              selected={o.key === selected.key}
              onSelect={() => {
                setSelectedPlan(o.key);
                posthog.capture('checkout_plan_row_selected', { plan: o.key });
              }}
            />
          ))}
        </div>
        <button
          onClick={selected.onBuy}
          disabled={isLoading}
          className="mt-3.5 w-full rounded-xl px-4 py-3.5 font-apotek text-[15px] font-bold uppercase tracking-widest text-white bg-[#E09F2E] hover:bg-amber-500 shadow-[0_6px_16px_rgba(224,159,46,0.35)] transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
        >
          {isLoading ? 'Przetwarzanie...' : `${ownsSomePlan ? 'Dokupuję' : 'Wybieram'} · ${selected.price}`}
        </button>
        <ul className="mt-5 space-y-3">
          {selected.features.map((feature, index) => (
            <FeatureItem key={index} feature={feature} checkClassName="text-[#E09F2E]" />
          ))}
          {/* Wspólny dla wszystkich planów — otwiera sheet z metodami nauki. */}
          <li className="flex items-start text-sm text-gray-700">
            <MdCheck className="mr-2 h-5 w-5 flex-shrink-0 text-[#E09F2E]" />
            <div>
              <p className="font-semibold text-[#233855]">Nauka oparta na badaniach naukowych</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Powtórki w odstępach, przeplatanie działów, poziom dopasowany do Ciebie ·{' '}
                <button
                  type="button"
                  onClick={() => { posthog.capture('checkout_learning_system_opened'); setShowFeaturesModal(true); }}
                  className="font-semibold text-[#233855] underline underline-offset-2"
                >
                  Jak to działa ›
                </button>
              </p>
            </div>
          </li>
        </ul>
      </div>
    );
  };


  // --- RENDEROWANIE WEB ---
  const renderWebOptions = IS_MOBILE_BUILD
    ? () => null
    : () => {
        const accessUntilText = format(ACCESS_END_DATE, "'Dostęp do' d MMMM yyyy", { locale: pl });

        const planId = isPromoActive ? 'matura_pp_2027_promo' : 'matura_pp_2027_regular';
        const planPrice = isPromoActive ? prices.matura_pp_2027_promo : prices.matura_pp_2027_regular;
        const planPriceRaw = isPromoActive ? rawPrices.matura_pp_2027_promo : rawPrices.matura_pp_2027_regular;

        // --- ROZSZERZENIE: samo / w pakiecie ---
        const prPlanId = isPromoActive ? 'matura_pr_2027_promo' : 'matura_pr_2027_regular';
        const prPrice = isPromoActive ? prices.matura_pr_2027_promo : prices.matura_pr_2027_regular;
        const prPriceRaw = isPromoActive ? rawPrices.matura_pr_2027_promo : rawPrices.matura_pr_2027_regular;

        const comboPlanId = isPromoActive ? 'matura_pp_pr_2027_promo' : 'matura_pp_pr_2027_regular';
        const comboPrice = isPromoActive ? prices.matura_pp_pr_2027_promo : prices.matura_pp_pr_2027_regular;
        const comboPriceRaw = isPromoActive ? rawPrices.matura_pp_pr_2027_promo : rawPrices.matura_pp_pr_2027_regular;

        const offers = [
          !hasMaturaAccess && {
            key: 'pp',
            name: 'Podstawa',
            price: planPrice,
            oldPrice: isPromoActive ? prices.matura_pp_2027_regular : null,
            features: featuresPP,
            onBuy: () => handleOpenSummary({
              title: isPromoActive ? 'Oferta powitalna - Podstawa 2027' : 'Podstawa 2027',
              price: planPrice,
              accessUntilText,
              action: () => performWebAction(planId, planPriceRaw / 100),
            }),
          },
          !hasMaturaAccess && !hasMaturaExtendedAccess && {
            key: 'combo',
            name: 'Podstawa + Rozszerzenie',
            price: comboPrice,
            oldPrice: isPromoActive ? prices.matura_pp_pr_2027_regular : null,
            badge: 'Najkorzystniej',
            note: savingsNote(planPriceRaw / 100, prPriceRaw / 100, comboPriceRaw / 100),
            features: featuresCombo,
            onBuy: () => handleOpenSummary({
              title: isPromoActive ? 'Oferta powitalna - Podstawa + Rozszerzenie' : 'Podstawa + Rozszerzenie 2027',
              price: comboPrice,
              accessUntilText,
              action: () => performWebAction(comboPlanId, comboPriceRaw / 100),
            }),
          },
          !hasMaturaExtendedAccess && {
            key: 'pr',
            name: 'Rozszerzenie',
            price: prPrice,
            oldPrice: isPromoActive ? prices.matura_pr_2027_regular : null,
            features: featuresPR,
            onBuy: () => handleOpenSummary({
              title: 'Rozszerzenie 2027',
              price: prPrice,
              accessUntilText,
              action: () => performWebAction(prPlanId, prPriceRaw / 100),
            }),
          },
        ];

        return (
          <div className="flex flex-col items-center gap-14">
            {renderPlanPicker(offers)}

            {!isPoprawkaExpired && (
              <>
                <div className="w-full max-w-sm">
                  <NavyPlanCard
                    title="Do Poprawki 2026"
                    badge="Druga Szansa"
                    price={prices.plan_poprawka_2026}
                    accessUntil="Dostęp do 31 sierpnia 2026"
                    features={featuresPoprawka}
                    onSelect={() => handleOpenSummary({
                      title: 'Do Poprawki 2026',
                      price: prices.plan_poprawka_2026,
                      accessUntilText: 'Dostęp do 31 sierpnia 2026',
                      action: () => performWebAction('matura_poprawka', rawPrices.plan_poprawka_2026 / 100),
                    })}
                    disabled={isLoading}
                  />
                </div>
              </>
            )}
          </div>
        );
      };

  // --- RENDEROWANIE NATIVE (MOBILE) ---
  const renderNativeOptions = () => {
    if (isLoading && packages.length === 0) return <p className="text-center mt-10">Ładowanie...</p>;
    if (!isLoading && packages.length === 0) return <p className="text-center mt-10 whitespace-pre-wrap">{error || "Brak planów."}</p>;

    const poprawkaPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_POPRAWKA);

    const prRegularPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PR_2027_REGULAR);
    const prPromoPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PR_2027_PROMO);
    const activePrPkg = (isPromoActive && prPromoPkg) ? prPromoPkg : prRegularPkg;

    const comboRegularPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PP_PR_2027_REGULAR);
    const comboPromoPkg = packages.find(p => p.product.identifier.trim() === PRODUCT_IDS.MATURA_PP_PR_2027_PROMO);
    const activeComboPkg = (isPromoActive && comboPromoPkg) ? comboPromoPkg : comboRegularPkg;

    const accessUntilText = format(ACCESS_END_DATE, "'Dostęp do' d MMMM yyyy", { locale: pl });

    const offers = [
      maturaPlan && maturaPlan.isNative && {
        key: 'pp',
        name: 'Podstawa',
        price: maturaPlan.price,
        oldPrice: maturaPlan.oldPrice,
        features: maturaPlan.features,
        onBuy: () => handleOpenSummary({
          title: isPromoActive ? 'Oferta powitalna - Podstawa 2027' : maturaPlan.title,
          price: maturaPlan.price,
          accessUntilText,
          action: () => handleNativePurchase(maturaPlan.pkg),
        }),
      },
      activeComboPkg && !hasMaturaAccess && !hasMaturaExtendedAccess && {
        key: 'combo',
        name: 'Podstawa + Rozszerzenie',
        price: activeComboPkg.product.priceString,
        oldPrice: (isPromoActive && comboPromoPkg && comboRegularPkg) ? comboRegularPkg.product.priceString : null,
        badge: 'Najkorzystniej',
        note: savingsNote(maturaPlan?.pkg?.product?.price, activePrPkg?.product?.price, activeComboPkg.product.price),
        features: featuresCombo,
        onBuy: () => handleOpenSummary({
          title: (isPromoActive && comboPromoPkg) ? 'Oferta powitalna - Podstawa + Rozszerzenie' : 'Podstawa + Rozszerzenie 2027',
          price: activeComboPkg.product.priceString,
          accessUntilText,
          action: () => handleNativePurchase(activeComboPkg),
        }),
      },
      activePrPkg && !hasMaturaExtendedAccess && {
        key: 'pr',
        name: 'Rozszerzenie',
        price: activePrPkg.product.priceString,
        oldPrice: (isPromoActive && prPromoPkg && prRegularPkg) ? prRegularPkg.product.priceString : null,
        features: featuresPR,
        onBuy: () => handleOpenSummary({
          title: 'Rozszerzenie 2027',
          price: activePrPkg.product.priceString,
          accessUntilText,
          action: () => handleNativePurchase(activePrPkg),
        }),
      },
    ];

    return (
      <div className="flex flex-col items-center">
        <div className="w-full flex flex-col items-center gap-14">
          {renderPlanPicker(offers)}

          {poprawkaPkg && !isPoprawkaExpired && (
            <div className="w-full max-w-sm">
              <NavyPlanCard
                title="Do Poprawki 2026"
                badge="Druga Szansa"
                price={poprawkaPkg.product.priceString}
                accessUntil="Dostęp do 31 sierpnia 2026"
                features={featuresPoprawka}
                onSelect={() => handleOpenSummary({
                  title: 'Do Poprawki 2026',
                  price: poprawkaPkg.product.priceString,
                  accessUntilText: 'Dostęp do 31 sierpnia 2026',
                  action: () => handleNativePurchase(poprawkaPkg),
                })}
                disabled={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Page showNav={false}>
      <div className="bg-white lg:bg-transparent w-full">
      <div className="flex max-w-2xl flex-col mx-auto w-full px-4 lg:px-0">
        <div className="flex items-center justify-between py-3 lg:mt-[4vh] lg:mb-2">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#233855] transition-colors"><MdArrowBack />Powrót</button>
        </div>

        <div className="w-full py-4">
          
          
          {!showSummaryModal && error && (
            <div className="my-6 p-3 text-center bg-red-100 border border-red-400 text-red-700 rounded-lg whitespace-pre-wrap" role="alert">{error}</div>
          )}

          <div className="w-full">
            <h1 className="font-apotek text-[28px] leading-none uppercase tracking-wider text-[#233855]">Matura 2027</h1>
            {!ownsSomePlan && (
              <p className="mt-1.5 text-sm text-[#233855]">
                Jedna płatność · dostęp do <span className="font-bold">{format(ACCESS_END_DATE, 'd MMMM yyyy', { locale: pl })}</span>
              </p>
            )}

            {ownsSomePlan && (
              <div className="mt-4 rounded-2xl border border-[#007D00]/20 bg-[#007D00]/[0.05] px-3.5 py-1">
                {hasMaturaAccess && <AccessStatusRow name="Podstawa" verb="aktywna" until={baseDate} />}
                {hasMaturaExtendedAccess && <AccessStatusRow name="Rozszerzenie" verb="aktywne" until={extendedExpiresAt} />}
              </div>
            )}

            {ownsAllPlans ? (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-[#233855]">Masz wszystko odblokowane.</span><br />
                  Powodzenia na maturze!
                </p>
                <button
                  onClick={() => navigate('/app')}
                  className="mt-4 w-full rounded-xl px-4 py-3.5 font-apotek text-[15px] font-bold uppercase tracking-widest text-white bg-[#233855] hover:bg-[#345081] transition-colors"
                >
                  Wracam do nauki
                </button>
              </div>
            ) : (
              <>
                {ownsSomePlan && (
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#E09F2E]">
                      {hasMaturaAccess ? 'Dokup rozszerzenie' : 'Dokup podstawę'}
                    </p>
                    <p className="mt-0.5 text-sm text-[#233855]">
                      Jedna płatność · dostęp do <span className="font-bold">{format(ACCESS_END_DATE, 'd MMMM yyyy', { locale: pl })}</span>
                    </p>
                  </div>
                )}
                <div className="mt-5">
                  <StoreReviews />
                </div>
              </>
            )}
          </div>

          {!ownsAllPlans && ((isNative || IS_MOBILE_BUILD) ? renderNativeOptions() : renderWebOptions())}

          <div className="mt-12 flex flex-col items-center space-y-4">
            {(isNative || IS_MOBILE_BUILD) && (
              <button onClick={handleRestorePurchases} disabled={isLoading} className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#233855] transition-colors disabled:opacity-50">
                <MdRestore className="text-lg" />
                Przywróć zakupy
              </button>
            )}
            
            <div className="w-full border-t border-gray-100 pt-6 text-center">
              {!ownsAllPlans && (
                <p className="text-xs text-gray-400 mb-2">
                  <MdSecurity className="inline-block mr-1 mb-0.5" />
                  Bezpieczna płatność {(isNative || IS_MOBILE_BUILD) ? "przez sklep aplikacji" : "przez operatora"}
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-gray-500">
                <a href="https://matematly.pl/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-[#233855] hover:underline transition-colors">
                  Regulamin
                </a>
                <span className="text-gray-300">•</span>
                <a href="https://matematly.pl/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-[#233855] hover:underline transition-colors">
                  Polityka Prywatności
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <div className="h-[5vh] flex-shrink-0" />
      
      {showSummaryModal && summaryData && (
         <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-slide-up">
                 <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-apotek text-lg uppercase tracking-widest text-[#233855]">Podsumowanie</h3>
                    <button onClick={() => !isLoading && setShowSummaryModal(false)} disabled={isLoading} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
                        <MdClose className="w-6 h-6" />
                    </button>
                </div>
                <div className="p-6">
                    {error && (
                         <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm text-center whitespace-pre-wrap">
                             {error}
                         </div>
                    )}
                    
                    <div className="space-y-5">
                        <div>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    {/* Tytul promo ("Oferta powitalna - <pakiet>") zostaje w danych dla PostHoga, tu pokazujemy sam pakiet. */}
                                    <p className="font-semibold text-[#233855]">{summaryData.title.split(' - ').pop()}</p>
                                    <p className="text-sm text-gray-500">{summaryData.accessUntilText}</p>
                                </div>
                                <p className="font-semibold text-[#233855] whitespace-nowrap">{summaryData.price}</p>
                            </div>
                            <div className="mt-4 flex items-baseline justify-between border-t border-gray-200 pt-4">
                                <span className="font-semibold text-gray-700">Do zapłaty</span>
                                <span className="text-2xl font-bold text-[#233855] whitespace-nowrap">{summaryData.price}</span>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="flex h-6 items-center">
                                <input
                                    id="modal-terms" 
                                    name="terms" 
                                    type="checkbox" 
                                    checked={termsAccepted} 
                                    onChange={(e) => {
                                        setTermsAccepted(e.target.checked);
                                        if(e.target.checked) posthog.capture('checkout_terms_accepted');
                                    }} 
                                    disabled={isLoading} 
                                    className="h-5 w-5 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer disabled:cursor-not-allowed" 
                                />
                            </div>
                            <div className="text-sm">
                                <label htmlFor="modal-terms" className="text-gray-700 cursor-pointer select-none">
                                    Oświadczam, że zapoznałem się z{' '}<a href="https://matematly.pl/terms-of-service" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#233855] underline underline-offset-2">regulaminem aplikacji</a>{' '}oraz{' '}<a href="https://matematly.pl/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#233855] underline underline-offset-2">polityką prywatności</a>{' '}oraz akceptuję regulamin<span className="text-red-600 ml-1">*</span>
                                </label>
                            </div>
                        </div>

                        <div>
                            <p className="text-[0.65rem] text-gray-500 leading-tight text-justify">
                                Administratorem danych osobowych zbieranych za pośrednictwem aplikacji jest <strong>MATEMATLY sp. z o.o. w Krakowie</strong>. Dane są lub mogą być przetwarzane w celach oraz na podstawach wskazanych szczegółowo w polityce prywatności (np. dokonywanie zakupów, marketing bezpośredni). Polityka prywatności zawiera pełną informację na temat przetwarzania danych przez administratora wraz z prawami przysługującymi osobie, której dane dotyczą. Szybki kontakt z administratorem: <a href="mailto:kontakt@matematly.pl" className="text-gray-700 hover:underline">kontakt@matematly.pl</a>.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col items-center gap-2">
                        <button
                          onClick={handleConfirmPurchase}
                          disabled={!termsAccepted || isLoading}
                          className={`w-full rounded-lg px-4 py-3 font-apotek text-sm font-bold uppercase tracking-widest transition-colors ${(termsAccepted && !isLoading) ? 'bg-[#E09F2E] text-white hover:bg-amber-500 shadow-md cursor-pointer' : 'bg-[#E09F2E]/15 text-[#A8761F] cursor-not-allowed'}`}
                        >
                          {isLoading ? "Przetwarzanie..." : "Kupuję i Płacę"}
                        </button>
                        <button onClick={() => setShowSummaryModal(false)} disabled={isLoading} className="min-h-[44px] px-6 py-3 text-sm text-gray-500 hover:text-[#233855] transition-colors disabled:opacity-50">Anuluj</button>
                    </div>
                </div>
            </div>
        </div>
      )}

      <LearningSystemSheet isOpen={showFeaturesModal} onClose={() => setShowFeaturesModal(false)} />
    </Page>
  );
};

export default Upgrade;